import { Button, Input, Modal, RadioGroup, useToast } from "@design";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage } from "@/api";
import { SectionEmpty, SectionLoading } from "@/components/section";
import type { Visibility } from "./useSpaceItems";
import { VisibilityChoice } from "./VisibilityChoice";

export interface AddOption {
  uuid: string;
  name: string;
  description?: string | null;
}

/** Pick one thing I can see (already-added ones are left out), choose who sees it, add it. */
export function AddItemModal({
  title,
  options,
  loading,
  onAdd,
  onClose,
}: Readonly<{
  title: string;
  options: AddOption[];
  loading: boolean;
  onAdd: (uuid: string, visibility: Visibility) => Promise<unknown>;
  onClose: () => void;
}>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.items.add" });
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<Visibility>("private");
  const [saving, setSaving] = useState(false);
  const term = search.trim().toLowerCase();
  const shown = options.filter((o) => !term || o.name.toLowerCase().includes(term));

  const add = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      await onAdd(picked, visibility);
      toast.success(t("added"));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t("failed")));
    } finally {
      setSaving(false);
    }
  };

  let list: ReactNode;
  if (loading) list = <SectionLoading rows={3} />;
  else if (options.length === 0) list = <SectionEmpty hint={t("none")} />;
  else if (shown.length === 0) list = <SectionEmpty hint={t("noMatch")} />;
  else
    list = (
      <div className="max-h-64 overflow-y-auto pr-1">
        <RadioGroup
          value={picked ?? undefined}
          onChange={setPicked}
          options={shown.map((o) => ({ value: o.uuid, label: o.name, description: o.description ?? undefined }))}
        />
      </div>
    );

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button variant="primary" disabled={!picked} loading={saving} onClick={() => void add()}>
            {t("add")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          aria-label={t("search")}
          placeholder={t("search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {list}
        <VisibilityChoice value={visibility} onChange={setVisibility} />
      </div>
    </Modal>
  );
}
